import React, { useState } from 'react';
import { Building2, Key, Mail, Lock, User, ArrowRight, CheckCircle2, AlertCircle, Phone, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function TenantRegister() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [invitationCode, setInvitationCode] = useState('');
  const [tenantInfo, setTenantInfo] = useState(null);
  const [form, setForm] = useState({
    admin_email: '',
    admin_password: '',
    admin_password_confirm: '',
    admin_name: '',
    company_phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const verifyCode = async () => {
    if (!invitationCode.trim()) {
      setError('Digite o código de convite');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/api/tenants/verify/${invitationCode.trim().toUpperCase()}`);
      
      if (res.data.valid) {
        if (res.data.tenant.already_registered) {
          setError('Esta empresa já possui um administrador cadastrado.');
          return;
        }
        setTenantInfo(res.data.tenant);
        setStep(2);
      } else {
        setError(res.data.error || 'Código de convite inválido');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao verificar código');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!form.admin_name.trim()) {
      setError('Digite o nome completo');
      return;
    }
    if (!form.admin_email.trim()) {
      setError('Digite o email');
      return;
    }
    if (form.admin_password.length < 6) {
      setError('Senha deve ter 6+ caracteres');
      return;
    }
    if (form.admin_password !== form.admin_password_confirm) {
      setError('As senhas não coincidem');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await axios.post(`${API}/api/tenants/register`, {
        invitation_code: invitationCode.trim().toUpperCase(),
        admin_email: form.admin_email.trim(),
        admin_password: form.admin_password,
        admin_name: form.admin_name.trim(),
        company_phone: form.company_phone.trim()
      });

      if (res.data.success) {
        if (res.data.token) {
          localStorage.setItem('token', res.data.token);
        }
        setSuccess(true);
        setTimeout(() => navigate('/login'), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao registrar');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50 p-4">
        <div className="bg-white p-6 rounded-2xl shadow-xl max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-3 animate-bounce">
            <CheckCircle2 size={32} />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Cadastro Realizado!</h2>
          <p className="text-gray-600 text-sm mb-4">Redirecionando...</p>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        {/* Header */}
        <div className="text-center mb-5">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Building2 size={24} className="text-white" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">
            {step === 1 ? 'Registro de Empresa' : 'Criar Conta Admin'}
          </h1>
          <p className="text-gray-500 text-xs">
            {step === 1 ? 'Código de convite' : tenantInfo?.name}
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-4">
          <div className={`flex items-center justify-center w-7 h-7 rounded-full font-bold text-xs ${step >= 1 ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>1</div>
          <div className={`w-12 h-0.5 ${step >= 2 ? 'bg-green-500' : 'bg-gray-200'}`}></div>
          <div className={`flex items-center justify-center w-7 h-7 rounded-full font-bold text-xs ${step >= 2 ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>2</div>
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Código de Convite</label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input 
                  type="text" 
                  value={invitationCode} 
                  onChange={e => setInvitationCode(e.target.value.toUpperCase())} 
                  placeholder="A1B2C3D4E5F6"
                  maxLength={12}
                  onKeyPress={e => e.key === 'Enter' && verifyCode()}
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm font-mono uppercase focus:ring-2 focus:ring-green-500 outline-none" 
                />
              </div>
            </div>

            {error && (
              <div className="p-2.5 bg-red-50 border-l-2 border-red-500 rounded text-xs text-red-700">
                {error}
              </div>
            )}

            <button 
              onClick={verifyCode}
              disabled={loading || !invitationCode.trim()}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-semibold text-sm hover:from-green-600 hover:to-emerald-700 disabled:opacity-50">
              {loading ? 'Verificando...' : 'Verificar Código'}
            </button>

            <div className="text-center pt-2 border-t border-gray-100">
              <Link to="/login" className="text-xs text-green-600 hover:text-green-700">Já tem conta? Login</Link>
            </div>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && tenantInfo && (
          <div className="space-y-3">
            {/* Info Empresa */}
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Building2 size={14} className="text-green-600" />
                <p className="font-semibold text-green-900 text-sm">{tenantInfo.name}</p>
              </div>
            </div>

            <form onSubmit={handleRegister} className="space-y-2.5">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Nome do Admin</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input 
                    type="text" 
                    value={form.admin_name} 
                    onChange={e => setForm({...form, admin_name: e.target.value})} 
                    placeholder="João Silva"
                    required
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input 
                    type="email" 
                    value={form.admin_email} 
                    onChange={e => setForm({...form, admin_email: e.target.value})} 
                    placeholder="admin@empresa.com"
                    required
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Telefone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input 
                    type="tel" 
                    value={form.company_phone} 
                    onChange={e => setForm({...form, company_phone: e.target.value})} 
                    placeholder="(00) 00000-0000"
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Senha</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input 
                    type="password" 
                    value={form.admin_password} 
                    onChange={e => setForm({...form, admin_password: e.target.value})} 
                    placeholder="Mín. 6 caracteres"
                    minLength={6}
                    required
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Confirmar Senha</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input 
                    type="password" 
                    value={form.admin_password_confirm} 
                    onChange={e => setForm({...form, admin_password_confirm: e.target.value})} 
                    placeholder="Repita a senha"
                    required
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                  />
                </div>
              </div>

              {error && (
                <div className="p-2.5 bg-red-50 border-l-2 border-red-500 rounded text-xs text-red-700">
                  {error}
                </div>
              )}

              <button 
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-semibold text-sm hover:from-green-600 hover:to-emerald-700 disabled:opacity-50">
                {loading ? 'Criando...' : 'Criar Conta'}
              </button>
            </form>

            <button 
              onClick={() => { setStep(1); setError(''); }}
              className="w-full flex items-center justify-center gap-1 text-xs text-gray-600 hover:text-gray-800 py-1.5">
              <ArrowLeft size={12} /> Voltar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default TenantRegister;